/**
 * The public API exposed as window.__NEEDLE_INSPECTOR__.
 * All methods return plain JSON-serializable objects
 * so they work with Playwright's page.evaluate().
 */

import type { IApplication } from "@needle-tools/editor-core/src/editor/services/application/common/application.js";
import type { IHierarchyNode, IHierarchyService } from "@needle-tools/editor-core/src/platform/hierarchy/common/hierarchy.js";
import type { IAIToolService } from "@needle-tools/editor-core/src/platform/ai/common/ai.js";
import type { IPropertyService, PropertyScope, PropertyViewMode } from "@needle-tools/editor-core/src/platform/properties/common/properties.js";
import type { IAnalyticsService } from "@needle-tools/editor-core/src/platform/analytics/common/analytics.js";
import { safeStringify } from "@needle-tools/editor-core/src/base/common/objects.js";

// Re-import enums as values (not just types)
import { PropertyScope as PropertyScopeEnum, PropertyViewMode as PropertyViewModeEnum } from "@needle-tools/editor-core/src/platform/properties/common/properties.js";


export interface SerializedNode {
    id: string;
    name: string;
    type: string;
    label: string;
    visible: boolean;
    selected: boolean;
    expanded: boolean;
    childCount: number;
    children?: SerializedNode[];
}

export interface ToolDefinition {
    name: string;
    description: string;
    inputSchema: object;
}

export interface NeedleInspectorAPI {
    /** True once the inspector has detected a Three.js scene and is ready to use. */
    ready: boolean;

    /** List all available inspector tools with their names, descriptions, and input schemas. */
    getTools(): Promise<ToolDefinition[]>;

    /** Call any tool by name with optional arguments. Returns the tool's result. */
    callTool(name: string, args?: object): Promise<any>;

    /** Get the full scene hierarchy as a serialized tree. */
    getHierarchy(maxDepth?: number): Promise<SerializedNode[]>;

    /** Get the currently selected nodes. */
    getSelectedNodes(): Promise<SerializedNode[]>;

    /** Select a node by its ID. */
    selectNode(id: string): Promise<{ success: boolean; message?: string }>;

    /** Search for nodes by name. */
    searchNodes(query: string, maxResults?: number): Promise<SerializedNode[]>;

    /** Get properties of a node by its ID. */
    getProperties(nodeId: string): Promise<any>;

    /** Find properties matching a query across nodes. Shorthand for the property_find tool. */
    findProperties(args: { nodeQuery?: string; nodesIds?: string[]; propertyQuery?: string; includeValues?: boolean }): Promise<any>;

    /** Read a specific property value. Shorthand for the property_read tool. */
    readProperty(nodeId: string, propertyPath: string): Promise<any>;
}


function serializeNode(node: IHierarchyNode, depth: number, maxDepth: number): SerializedNode {
    const result: SerializedNode = {
        id: node.id,
        name: node.name,
        type: node.type,
        label: node.label,
        visible: node.visible,
        selected: node.selected,
        expanded: node.expanded,
        childCount: node.children.length,
    };

    if (depth < maxDepth && node.children.length > 0) {
        result.children = node.children.map(child => serializeNode(child, depth + 1, maxDepth));
    }

    return result;
}


export function createInspectorAPI(app: IApplication, analytics?: IAnalyticsService): NeedleInspectorAPI {

    const hierarchy = app.hierarchyService;
    const toolService = app.ai?.toolService;
    const propertyService = app.propertyService;

    const api: NeedleInspectorAPI = {

        ready: false,

        async getTools(): Promise<ToolDefinition[]> {
            if (!toolService) return [];
            const tools = await toolService.getAvailableTools();
            return tools.map(t => ({
                name: t.name,
                description: t.description,
                inputSchema: t.parametersJSONSchema,
            }));
        },

        async callTool(name: string, args?: object): Promise<any> {
            if (!toolService) return { error: "Tool service not available" };
            try {
                analytics?.logEvent("agent.callTool", { tool: name });
                const result = await toolService.callTool(name, args || {});
                // Parse aiMessage if it's a JSON string, otherwise return as-is
                if (result.result.aiMessage) {
                    try {
                        return JSON.parse(result.result.aiMessage);
                    } catch {
                        return result.result.aiMessage;
                    }
                }
                return result.result.message || result.result;
            } catch (err: any) {
                console.warn(`[Needle Inspector] callTool('${name}') failed:`, err);
                analytics?.logEvent("agent.callTool.error", { tool: name, error: err?.message || String(err) });
                return { error: err?.message || String(err) };
            }
        },

        async getHierarchy(maxDepth: number = 10): Promise<SerializedNode[]> {
            if (!hierarchy) return [];
            analytics?.logEvent("agent.getHierarchy");
            return hierarchy.roots.map(root => serializeNode(root, 0, maxDepth));
        },

        async getSelectedNodes(): Promise<SerializedNode[]> {
            if (!hierarchy) return [];
            const selection = hierarchy.getSelectedNodes();
            return selection.map(node => serializeNode(node, 0, 2));
        },

        async selectNode(id: string): Promise<{ success: boolean; message?: string }> {
            if (!hierarchy) return { success: false, message: "Hierarchy not available" };
            const node = hierarchy.findById(id);
            if (!node) return { success: false, message: `Node '${id}' not found` };
            node.selected = true;
            return { success: true };
        },

        async searchNodes(query: string, maxResults: number = 20): Promise<SerializedNode[]> {
            if (!hierarchy) return [];
            analytics?.logEvent("agent.searchNodes", { query });
            const results = await hierarchy.search(query, maxResults);
            return (results ?? []).map(node => serializeNode(node, 0, 1));
        },

        async getProperties(nodeId: string): Promise<any> {
            return api.callTool("property_find", {
                nodesIds: [nodeId],
                includeValues: true,
                maxValueSize: 500,
            });
        },

        async findProperties(args: { nodeQuery?: string; nodesIds?: string[]; propertyQuery?: string; includeValues?: boolean }): Promise<any> {
            return api.callTool("property_find", args);
        },

        async readProperty(nodeId: string, propertyPath: string): Promise<any> {
            return api.callTool("property_read", { nodeId, propertyPath });
        },
    };

    return api;
}
